import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import {
  Archive,
  ArrowDownAZ,
  ArrowUpAZ,
  BarChart3,
  CalendarDays,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  Columns3,
  Download,
  FileSpreadsheet,
  Filter,
  GripVertical,
  Import,
  ListFilter,
  Loader2,
  Maximize2,
  Minimize2,
  MoreHorizontal,
  MoreVertical,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  Settings2,
  Trash2,
  Upload,
  UserPlus,
  X,
} from 'lucide-react';
import { apiFetch } from '../services/api';
import { useNavigate, useParams } from 'react-router-dom';
import './DataBase.css';

const CORE_COLUMNS = [
  { key: 'name', label: 'Nome', width: 230 },
  { key: 'phone', label: 'Telefone', width: 155 },
  { key: 'sector', label: 'Setor', width: 150 },
  { key: 'registered_at', label: 'Data de cadastro', width: 165 },
  { key: 'source', label: 'Origem', width: 145 },
  { key: 'observations', label: 'Observações', width: 220 },
];

// Colunas próprias da Base Total de Leads (MD4).
const CONSOLIDATED_COLUMNS = [
  { key: 'name', label: 'Nome', width: 230, order: 0, visible: true },
  { key: 'phone', label: 'Telefone', width: 155, order: 1, visible: true },
  { key: 'sector', label: 'Setor', width: 150, order: 2, visible: true },
  { key: 'registered_at', label: 'Data de cadastro', width: 165, order: 3, visible: true },
  { key: 'bases_associadas', label: 'Bases associadas', width: 220, order: 4, visible: true },
];

const EDITABLE_COLUMNS = new Set(['name', 'phone', 'sector', 'registered_at', 'source', 'observations']);
const SORTABLE_COLUMNS = new Set(['name', 'phone', 'sector', 'registered_at']);
const PAGE_SIZES = [10, 25, 50];

const BASE_COLORS = [
  '#4D56D8', '#2563EB', '#0EA5E9', '#0891B2', '#14B8A6', '#10B981', '#22C55E', '#84CC16', '#EAB308', '#F59E0B', '#F97316', '#EF4444', '#DC2626', '#E11D48', '#EC4899', '#D946EF', '#A855F7', '#7C3AED', '#64748B', '#334155',
];
const IMPORT_FIELDS = [
  { key: 'name', label: 'Nome', required: true },
  { key: 'phone', label: 'Telefone', required: false },
  { key: 'sector', label: 'Setor' },
  { key: 'registered_at', label: 'Data de cadastro' },
  { key: 'source', label: 'Origem' },
  { key: 'observations', label: 'Observações' },
];

const today = () => {
  const date = new Date();
  const offset = date.getTimezoneOffset();
  return new Date(date.getTime() - offset * 60 * 1000).toISOString().slice(0, 10);
};

const formatDate = (value) => {
  if (!value) return '—';
  const raw = String(value);
  const date = new Date(raw.length === 10 ? `${raw}T12:00:00` : raw);
  if (Number.isNaN(date.getTime())) return raw;
  return new Intl.DateTimeFormat('pt-BR').format(date);
};

const inputDate = (value) => (value ? String(value).slice(0, 10) : '');

const formatDateTime = (value) => {
  if (!value) return 'Ainda não atualizado';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Ainda não atualizado';
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(date);
};

const slugify = (value) => String(value || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .trim()
  .replace(/[^a-z0-9]+/g, '_')
  .replace(/^_|_$/g, '') || `campo_${Date.now()}`;

const humanizeKey = (key) => String(key || '')
  .replace(/^custom[_.-]?/i, '')
  .replace(/[_-]+/g, ' ')
  .replace(/\b\w/g, (letter) => letter.toUpperCase());

const valueForColumn = (row, column) => {
  if (column.key === 'bases_associadas') return Array.isArray(row?.base_names) ? row.base_names.join(', ') : '';
  if (CORE_COLUMNS.some((item) => item.key === column.key)) return row?.[column.key] ?? '';
  return row?.custom_fields?.[column.key] ?? '';
};

const labelForColumn = (key) => [...CORE_COLUMNS, ...CONSOLIDATED_COLUMNS].find((column) => column.key === key)?.label || humanizeKey(key);

const normalizeColumns = (config, customKeys = []) => {
  let configured = config;
  if (typeof configured === 'string') {
    try { configured = JSON.parse(configured); } catch { configured = null; }
  }
  if (configured && !Array.isArray(configured) && Array.isArray(configured.columns)) configured = configured.columns;
  const source = Array.isArray(configured) && configured.length
    ? configured
    : CORE_COLUMNS.map((column, order) => ({ ...column, order, visible: true }));
  const normalized = source.map((column, index) => {
    const key = typeof column === 'string' ? column : column.key;
    return {
      key,
      label: typeof column === 'string' ? labelForColumn(key) : (column.label || labelForColumn(key)),
      visible: typeof column === 'string' ? true : column.visible !== false,
      width: Number(typeof column === 'string' ? 150 : column.width) || 150,
      order: Number(typeof column === 'string' ? index : column.order ?? index),
    };
  }).filter((column) => column.key);
  const existing = new Set(normalized.map((column) => column.key));
  customKeys.filter((key) => !existing.has(key)).forEach((key, index) => {
    normalized.push({ key, label: humanizeKey(key), visible: true, width: 160, order: normalized.length + index });
  });
  return normalized.sort((a, b) => a.order - b.order).map((column, index) => ({ ...column, order: index }));
};

const queryString = (params) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') query.set(key, value);
  });
  return query.toString();
};

const errorMessage = (error, fallback) => error?.message || fallback;

const getCurrentUserRoles = () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) return [];

    const payload = JSON.parse(
      atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))
    );

    const roles = payload.roles || payload.role || payload.perfil || [];
    return Array.isArray(roles) ? roles.map((role) => String(role).toLowerCase()) : [String(roles).toLowerCase()];
  } catch (_) {
    return [];
  }
};

function StatCard({ icon: Icon, label, value, hint, tone }) {
  return (
    <div className={`db-stat-card db-stat-${tone}`}>
      <div className="db-stat-icon"><Icon size={18} /></div>
      <div>
        <span className="db-stat-label">{label}</span>
        <strong>{value}</strong>
        <small>{hint}</small>
      </div>
    </div>
  );
}

function EmptyState({ searchActive, onClear }) {
  return (
    <div className="db-empty-state">
      <div className="db-empty-icon"><Archive size={24} /></div>
      <h3>{searchActive ? 'Nenhum contato encontrado' : 'Sua base ainda está vazia'}</h3>
      <p>{searchActive ? 'Ajuste os filtros ou tente outra busca.' : 'Cadastre um lead ou importe uma planilha para começar.'}</p>
      {searchActive && <button className="db-button db-button-ghost" type="button" onClick={onClear}>Limpar filtros</button>}
    </div>
  );
}

function Modal({ title, eyebrow, onClose, children, className = '' }) {
  return (
    <div className="db-modal-backdrop" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget) onClose();
    }}>
      <section className={`db-modal ${className}`} role="dialog" aria-modal="true" aria-label={title}>
        <header className="db-modal-header">
          <div>
            {eyebrow && <span className="db-eyebrow">{eyebrow}</span>}
            <h2>{title}</h2>
          </div>
          <button type="button" className="db-icon-button" onClick={onClose} aria-label="Fechar"><X size={18} /></button>
        </header>
        {children}
      </section>
    </div>
  );
}

export default function DataBase() {
  const navigate = useNavigate();
  const { baseId: baseIdDaRota } = useParams();

  const [bases, setBases] = useState([]);
  const [activeBaseId, setActiveBaseId] = useState(baseIdDaRota || 'all');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [uniqueContacts, setUniqueContacts] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState(null);
  const [search, setSearch] = useState('');
  const [sector, setSector] = useState('');
  const [associatedBase, setAssociatedBase] = useState('');
  const [source, setSource] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [leadOpen, setLeadOpen] = useState(false);
  const [baseDialogOpen, setBaseDialogOpen] = useState(false);
  const [baseName, setBaseName] = useState('');
  const [editBaseOpen, setEditBaseOpen] = useState(false);
  const [editBase, setEditBase] = useState(null);
  const [editBaseName, setEditBaseName] = useState('');
  const [editBaseColor, setEditBaseColor] = useState('#4D56D8');
  const [savingBaseEdit, setSavingBaseEdit] = useState(false);
  const [savingLead, setSavingLead] = useState(false);
  const [leadForm, setLeadForm] = useState({
    name: '', phone: '', sector: '', registered_at: today(), source: '', base_id: '', observations: '',
  });
  const [importOpen, setImportOpen] = useState(false);
  const [importStep, setImportStep] = useState('file');
  const [importFile, setImportFile] = useState(null);
  const [importBaseId, setImportBaseId] = useState('');
  const [importHeaders, setImportHeaders] = useState([]);
  const [importRows, setImportRows] = useState([]);
  const [importMapping, setImportMapping] = useState({});
  const [importResult, setImportResult] = useState(null);
  const [importRecords, setImportRecords] = useState([]);
  const [importBusy, setImportBusy] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [exportMode, setExportMode] = useState('filtered');
  const [exportFormat, setExportFormat] = useState('xlsx');
  const [exportColumns, setExportColumns] = useState([]);
  const [exportBusy, setExportBusy] = useState(false);
  const [columnsOpen, setColumnsOpen] = useState(false);
  const [ampliado, setAmpliado] = useState(false);
  const [canDeleteBase] = useState(() => getCurrentUserRoles().some((role) =>
    ['admin', 'administrador', 'marketing'].includes(
      String(role).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
    )
  ));
  const [menuBaseAberto, setMenuBaseAberto] = useState(null);
  const [filtroBaseHome, setFiltroBaseHome] = useState('');
  const [buscaBaseHome, setBuscaBaseHome] = useState('');
  const [draftColumns, setDraftColumns] = useState([]);
  const [customColumnName, setCustomColumnName] = useState('');
  const [savingColumns, setSavingColumns] = useState(false);
  const autosaveTimers = useRef({});
  const itemsRef = useRef(items);
  const noticeTimer = useRef(null);

  useEffect(() => { itemsRef.current = items; }, [items]);

  const notify = useCallback((message, type = 'success') => {
    setNotice({ message, type });
    if (noticeTimer.current) clearTimeout(noticeTimer.current);
    noticeTimer.current = setTimeout(() => setNotice(null), 4500);
  }, []);

  const loadBases = useCallback(async (preferredId) => {
    try {
      const response = await apiFetch('/data-base/bases');
      const nextBases = Array.isArray(response?.bases)
        ? response.bases.map((base) => ({ ...base, count: Number(base.count ?? base.contact_count ?? 0) }))
        : [];
      setBases(nextBases);
      setUniqueContacts(Number(response?.unique_contacts ?? response?.contacts_count ?? 0));
      if (preferredId && nextBases.some((base) => String(base.id) === String(preferredId))) {
        setActiveBaseId(String(preferredId));
      } else if (activeBaseId !== 'all' && !nextBases.some((base) => String(base.id) === String(activeBaseId))) {
        setActiveBaseId('all');
      }
      try {
        const countResponse = await apiFetch('/data-base/contacts?' + queryString({ baseId: 'all', page: 1, pageSize: 1 }));
        if (typeof countResponse?.total === 'number') setUniqueContacts(countResponse.total);
      } catch { /* A listagem principal continua mesmo sem a métrica auxiliar. */ }
      return nextBases;
    } catch (loadError) {
      setError(errorMessage(loadError, 'Não foi possível carregar suas bases.'));
      return [];
    }
  }, [activeBaseId]);

  const loadContacts = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await apiFetch('/data-base/contacts?' + queryString({
        baseId: activeBaseId,
        associatedBase: activeBaseId === 'all' ? associatedBase : '',
        search, sector, source, dateFrom, dateTo, sortBy, sortDir, page, pageSize,
      }));
      setItems(Array.isArray(response?.items) ? response.items : []);
      setTotal(Number(response?.total || 0));
      if (response?.page && Number(response.page) !== page) setPage(Number(response.page));
    } catch (loadError) {
      setItems([]);
      setTotal(0);
      setError(errorMessage(loadError, 'Não foi possível carregar os contatos.'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [activeBaseId, associatedBase, dateFrom, dateTo, page, pageSize, search, sector, sortBy, sortDir, source]);

  useEffect(() => {
    setActiveBaseId(baseIdDaRota || 'all');
    window.scrollTo(0, 0);
  }, [baseIdDaRota]);

  useEffect(() => { loadBases(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadContacts(); }, [loadContacts]);
  useEffect(() => {
    setPage(1);
    setSelectedIds(new Set());
  }, [activeBaseId, associatedBase, dateFrom, dateTo, search, sector, sortBy, sortDir, source]);

  const activeBase = useMemo(() => bases.find((base) => String(base.id) === String(activeBaseId)), [activeBaseId, bases]);
  const basesFiltradas = useMemo(() => {
    const normalizar = (valor) => String(valor || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
    const termo = normalizar(buscaBaseHome);
    return bases.filter((base) =>
      (!filtroBaseHome || String(base.id) === String(filtroBaseHome)) &&
      (!termo || normalizar(base.name).includes(termo))
    );
  }, [bases, filtroBaseHome, buscaBaseHome]);
  const customKeys = useMemo(() => {
    const keys = new Set();
    items.forEach((item) => Object.keys(item.custom_fields || {}).forEach((key) => keys.add(key)));
    return [...keys];
  }, [items]);
  const columns = useMemo(() => (
    activeBaseId === 'all'
      ? normalizeColumns(CONSOLIDATED_COLUMNS, [])
      : normalizeColumns(activeBase?.columns_config, customKeys)
  ), [activeBaseId, activeBase, customKeys]);
  const visibleColumns = useMemo(() => columns.filter((column) => column.visible), [columns]);
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const selectedRows = useMemo(() => items.filter((item) => selectedIds.has(item.id)), [items, selectedIds]);
  const allPageSelected = items.length > 0 && items.every((item) => selectedIds.has(item.id));
  const hasFilters = Boolean(search || sector || source || dateFrom || dateTo || (activeBaseId === 'all' && associatedBase));
  const filterOptions = useMemo(() => {
    const sectors = new Set();
    const sources = new Set();
    items.forEach((item) => { if (item.sector) sectors.add(item.sector); if (item.source) sources.add(item.source); });
    return { sectors: [...sectors].sort(), sources: [...sources].sort() };
  }, [items]);

  const refresh = () => { setRefreshing(true); loadBases(); loadContacts(); };
  const clearFilters = () => {
    setSearch(''); setSector(''); setSource(''); setDateFrom(''); setDateTo(''); setAssociatedBase(''); setSortBy('name'); setSortDir('asc');
  };
  const changeSort = (key) => {
    if (!SORTABLE_COLUMNS.has(key)) return;
    if (sortBy === key) setSortDir((current) => current === 'asc' ? 'desc' : 'asc');
    else { setSortBy(key); setSortDir('asc'); }
  };
  const abrirBase = (id) => {
    if (String(id) === 'all') {
      navigate('/data-base');
      return;
    }

    navigate(`/data-base/${id}`);
  };

  const consultarBaseHome = () => {
    if (filtroBaseHome) { abrirBase(filtroBaseHome); return; }
    if (basesFiltradas.length === 1) { abrirBase(basesFiltradas[0].id); return; }
    if (!basesFiltradas.length) { notify('Nenhuma base encontrada com esse filtro.', 'error'); return; }
    notify(`${basesFiltradas.length} bases encontradas. Escolha uma para abrir.`);
  };

  const excluirBase = async (base) => {
    if (!canDeleteBase) {
      notify('Somente administradores ou usuários de Marketing podem excluir bases.', 'error');
      return;
    }

    const confirmacao = window.confirm(
      `Excluir a base "${base.name}"?\n\n` +
      `Os contatos que também pertencem a outras bases serão preservados. ` +
      `Contatos exclusivos desta base serão excluídos definitivamente.`
    );

    if (!confirmacao) return;

    try {
      await apiFetch(`/data-base/bases/${base.id}`, {
        method: 'DELETE',
      });

      if (String(activeBaseId) === String(base.id)) {
        navigate('/data-base');
      }

      notify(`Base "${base.name}" excluída com sucesso.`);
      await Promise.all([loadBases(), loadContacts()]);
    } catch (deleteError) {
      notify(
        errorMessage(deleteError, 'Não foi possível excluir a base.'),
        'error'
      );
    }
  };

  const abrirEdicaoBase = (base) => {
    setEditBase(base);
    setEditBaseName(base.name || '');
    setEditBaseColor(base.color || '#4D56D8');
    setMenuBaseAberto(null);
    setEditBaseOpen(true);
  };

  const salvarEdicaoBase = async (event) => {
    event.preventDefault();

    const nome = editBaseName.trim();
    if (!nome) {
      notify('Informe o nome da base.', 'error');
      return;
    }

    setSavingBaseEdit(true);

    try {
      const response = await apiFetch(`/data-base/bases/${editBase.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          name: nome,
          color: editBaseColor,
        }),
      });

      const atualizado = response?.base || {
        ...editBase,
        name: nome,
        color: editBaseColor,
      };

      setBases((atual) => atual.map((base) =>
        String(base.id) === String(editBase.id)
          ? { ...base, ...atualizado, name: nome, color: editBaseColor }
          : base
      ));

      setEditBaseOpen(false);
      setEditBase(null);
      notify('Base atualizada com sucesso.');
    } catch (error) {
      notify(errorMessage(error, 'Não foi possível atualizar a base.'), 'error');
    } finally {
      setSavingBaseEdit(false);
    }
  };

  const selectBase = (id) => { setActiveBaseId(String(id)); if (String(id) !== 'all') setAssociatedBase(''); setPage(1); setSelectedIds(new Set()); };
  const toggleSelection = (id) => setSelectedIds((current) => {
    const next = new Set(current);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const togglePageSelection = () => setSelectedIds((current) => {
    const next = new Set(current);
    if (allPageSelected) items.forEach((item) => next.delete(item.id));
    else items.forEach((item) => next.add(item.id));
    return next;
  });

  const baseForForm = activeBaseId !== 'all' ? activeBaseId : (bases[0]?.id ? String(bases[0].id) : '');
  const openLead = () => {
    setLeadForm({ name: '', phone: '', sector: '', registered_at: today(), source: '', base_id: baseForForm, observations: '' });
    setLeadOpen(true);
  };
  const submitLead = async (event) => {
    event.preventDefault();
    if (!leadForm.name.trim()) { notify('Informe o nome do lead.', 'error'); return; }
    if (!leadForm.base_id) { notify('Crie ou selecione uma base de destino.', 'error'); return; }
    setSavingLead(true);
    try {
      await apiFetch('/data-base/contacts', {
        method: 'POST',
        body: JSON.stringify({ ...leadForm, name: leadForm.name.trim(), custom_fields: {} }),
      });
      setLeadOpen(false);
      notify('Lead salvo. Telefones existentes são atualizados sem duplicação.');
      await Promise.all([loadContacts(), loadBases()]);
    } catch (saveError) { notify(errorMessage(saveError, 'Não foi possível salvar o lead.'), 'error'); }
    finally { setSavingLead(false); }
  };

  const patchContact = useCallback(async (row) => {
    const baseId = activeBaseId !== 'all' ? activeBaseId : (row.base_ids?.[0] || bases[0]?.id || 'all');
    await apiFetch(`/data-base/contacts/${row.id}`, {
      method: 'PATCH',
      body: JSON.stringify({
        name: row.name, phone: row.phone, sector: row.sector, registered_at: row.registered_at,
        source: row.source, observations: row.observations, custom_fields: row.custom_fields || {}, base_id: baseId,
      }),
    });
  }, [activeBaseId, bases]);

  const scheduleInlineSave = (id, field, value, custom = false) => {
    const timerKey = `${id}:${field}`;
    if (autosaveTimers.current[timerKey]) clearTimeout(autosaveTimers.current[timerKey]);
    autosaveTimers.current[timerKey] = setTimeout(async () => {
      const current = itemsRef.current.find((item) => item.id === id);
      if (!current) return;
      const next = custom
        ? { ...current, custom_fields: { ...(current.custom_fields || {}), [field]: value } }
        : { ...current, [field]: value };
      try { await patchContact(next); }
      catch (saveError) { notify(errorMessage(saveError, 'Não foi possível salvar esta alteração.'), 'error'); }
      finally { delete autosaveTimers.current[timerKey]; }
    }, 650);
  };
  const updateInline = (id, field, value, custom = false) => {
    setItems((current) => current.map((item) => {
      if (item.id !== id) return item;
      return custom
        ? { ...item, custom_fields: { ...(item.custom_fields || {}), [field]: value } }
        : { ...item, [field]: value };
    }));
    scheduleInlineSave(id, field, value, custom);
  };

  const removeContacts = async (ids) => {
    if (!ids.length) return;
    const permanent = activeBaseId === 'all';
    const confirmation = permanent
      ? 'Esta ação excluirá definitivamente os contatos selecionados de todas as bases. Continuar?'
      : `Remover ${ids.length} contato(s) desta base? O contato seguirá disponível em outras bases.`;
    if (!window.confirm(confirmation)) return;
    try {
      await apiFetch('/data-base/contacts', {
        method: 'DELETE',
        body: JSON.stringify({ contact_ids: ids, base_id: permanent ? 'all' : activeBaseId }),
      });
      setSelectedIds(new Set());
      notify(permanent ? 'Contatos excluídos definitivamente.' : 'Contatos removidos desta base.');
      await Promise.all([loadContacts(), loadBases()]);
    } catch (deleteError) { notify(errorMessage(deleteError, 'Não foi possível remover os contatos.'), 'error'); }
  };

  const submitBase = async (event) => {
    event.preventDefault();
    if (!baseName.trim()) return;
    try {
      const created = await apiFetch('/data-base/bases', { method: 'POST', body: JSON.stringify({ name: baseName.trim() }) });
      const createdId = created?.id || created?.base?.id;
      setBaseDialogOpen(false); setBaseName('');
      const nextBases = await loadBases(createdId);
      if (createdId || nextBases.length) setActiveBaseId(String(createdId || nextBases[nextBases.length - 1].id));
      notify('Nova base criada.');
    } catch (saveError) { notify(errorMessage(saveError, 'Não foi possível criar a base.'), 'error'); }
  };

  const openImport = () => {
    setImportFile(null); setImportBaseId(baseForForm); setImportHeaders([]); setImportRows([]); setImportMapping({}); setImportResult(null); setImportRecords([]); setImportStep('file'); setImportOpen(true);
  };
  const guessMapping = (headers) => {
    const normalized = headers.map((header) => ({ original: header, value: String(header).toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '') }));
    const patterns = {
      name: ['nome', 'name', 'cliente', 'contato'], phone: ['telefone', 'phone', 'celular', 'fone', 'whatsapp'], sector: ['setor', 'segmento', 'area'],
      registered_at: ['data cadastro', 'data de cadastro', 'cadastro', 'registered', 'data'], source: ['origem', 'source', 'canal'], observations: ['observacao', 'observações', 'obs', 'notas'],
    };
    const guessed = {};
    Object.entries(patterns).forEach(([field, choices]) => {
      const found = normalized.find(({ value }) => choices.some((choice) => value.includes(choice)));
      if (found) guessed[field] = found.original;
    });
    return guessed;
  };
  const readImportFile = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (loadEvent) => {
      try {
        const workbook = XLSX.read(loadEvent.target.result, { type: 'array', cellDates: true });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const matrix = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
        const headers = (matrix[0] || []).map((header, index) => String(header || `Coluna ${index + 1}`));
        const rows = matrix.slice(1).filter((row) => row.some((cell) => String(cell ?? '').trim() !== '')).map((row) => headers.reduce((acc, header, index) => {
          acc[header] = row[index] instanceof Date ? row[index].toISOString().slice(0, 10) : (row[index] ?? '');
          return acc;
        }, {}));
        setImportFile(file); setImportHeaders(headers); setImportRows(rows); setImportMapping(guessMapping(headers)); setImportStep('mapping');
      } catch (parseError) { notify(errorMessage(parseError, 'Não foi possível ler este arquivo.'), 'error'); }
    };
    reader.readAsArrayBuffer(file);
  };
  const normalizeImportDate = (value) => {
    const raw = String(value ?? '').trim();
    const br = /^(\d{1,2})[\/.-](\d{1,2})[\/.-](\d{4})$/.exec(raw);
    if (br) return `${br[3]}-${br[2].padStart(2, '0')}-${br[1].padStart(2, '0')}`;
    const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw);
    return iso ? `${iso[1]}-${iso[2]}-${iso[3]}` : raw;
  };

  const mappedImportRecords = useMemo(() => importRows.map((row) => {
    const record = {};
    IMPORT_FIELDS.forEach(({ key }) => { record[key] = importMapping[key] ? row[importMapping[key]] : ''; });
    record.registered_at = normalizeImportDate(record.registered_at);
    record.custom_fields = {};
    return record;
  }), [importMapping, importRows]);
  const validateImport = async () => {
    if (!importBaseId) { notify('Selecione ou crie uma base de destino antes de importar.', 'error'); return; }
    if (!importMapping.name) { notify('Mapeie a coluna Nome antes de validar.', 'error'); return; }
    const records = mappedImportRecords.filter((record) => record.name || record.phone);
    setImportBusy(true);
    try {
      const response = await apiFetch('/data-base/imports/validate', { method: 'POST', body: JSON.stringify({ base_id: importBaseId, records }) });
      const validRows = Array.isArray(response?.validRows) ? response.validRows : records;
      const normalizedRows = validRows.map((row) => ({
        name: row.name, phone: row.phone || '', sector: row.sector || '', registered_at: row.registered_at || '',
        source: row.source || '', observations: row.observations || '', custom_fields: row.custom_fields || {},
      }));
      setImportRecords(normalizedRows);
      setImportResult(response || { validRows: normalizedRows, invalidRows: [], duplicates: [] });
      setImportStep('result');
    } catch (validateError) { notify(errorMessage(validateError, 'Não foi possível validar o arquivo.'), 'error'); }
    finally { setImportBusy(false); }
  };
  const commitImport = async () => {
    if (!importBaseId || !importRecords.length) return;
    setImportBusy(true);
    try {
      await apiFetch('/data-base/imports/commit', { method: 'POST', body: JSON.stringify({ base_id: importBaseId, records: importRecords }) });
      setImportOpen(false); notify(`${importRecords.length} registro(s) importado(s) com sucesso.`); await Promise.all([loadContacts(), loadBases()]);
    } catch (commitError) { notify(errorMessage(commitError, 'Não foi possível concluir a importação.'), 'error'); }
    finally { setImportBusy(false); }
  };

  const openExport = () => { setExportColumns(visibleColumns.map((column) => column.key)); setExportMode(selectedIds.size ? 'selected' : 'filtered'); setExportOpen(true); };
  const exportRows = async () => {
    const common = {
      baseId: activeBaseId,
      ...(exportMode === 'filtered' && activeBaseId === 'all' && associatedBase ? { associatedBase } : {}),
      ...(exportMode === 'filtered' ? { search, sector, source, dateFrom, dateTo, sortBy, sortDir } : {}),
      ...(exportMode === 'selected' ? { contactIds: [...selectedIds].join(',') } : {}),
    };
    const first = await apiFetch('/data-base/contacts?' + queryString({ ...common, page: 1, pageSize: 10000 }));
    const rows = Array.isArray(first?.items) ? [...first.items] : [];
    const pages = Math.ceil(Number(first?.total || 0) / 10000);
    for (let pageNumber = 2; pageNumber <= pages; pageNumber += 1) {
      const next = await apiFetch('/data-base/contacts?' + queryString({ ...common, page: pageNumber, pageSize: 10000 }));
      if (Array.isArray(next?.items)) rows.push(...next.items);
    }
    return rows;
  };
  const runExport = async () => {
    if (!exportColumns.length) { notify('Escolha ao menos uma coluna para exportar.', 'error'); return; }
    setExportBusy(true);
    try {
      const rows = await exportRows();
      const chosen = visibleColumns.filter((column) => exportColumns.includes(column.key));
      const data = rows.map((row) => chosen.reduce((acc, column) => { acc[column.label] = valueForColumn(row, column); return acc; }, {}));
      const worksheet = XLSX.utils.json_to_sheet(data);
      const workbook = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(workbook, worksheet, 'Contatos');
      const suffix = exportMode === 'selected' ? 'selecionados' : exportMode === 'all' ? 'todos' : 'filtrados';
      XLSX.writeFile(workbook, `contatos-${suffix}.${exportFormat}`);
      setExportOpen(false); notify(`${rows.length} registro(s) exportado(s).`);
    } catch (exportError) { notify(errorMessage(exportError, 'Não foi possível exportar os contatos.'), 'error'); }
    finally { setExportBusy(false); }
  };

  const openColumns = () => {
    if (activeBaseId === 'all') { notify('Selecione uma base para configurar suas colunas.', 'error'); return; }
    setDraftColumns(columns.map((column) => ({ ...column }))); setCustomColumnName(''); setColumnsOpen(true);
  };
  const saveColumns = async () => {
    setSavingColumns(true);
    try {
      const columnsConfig = draftColumns.map((column, order) => ({ ...column, order }));
      await apiFetch(`/data-base/bases/${activeBaseId}/columns`, { method: 'PATCH', body: JSON.stringify({ columns_config: columnsConfig }) });
      setBases((current) => current.map((base) => String(base.id) === String(activeBaseId) ? { ...base, columns_config: columnsConfig } : base));
      setColumnsOpen(false); notify('Colunas da base atualizadas.');
    } catch (saveError) { notify(errorMessage(saveError, 'Não foi possível salvar as colunas.'), 'error'); }
    finally { setSavingColumns(false); }
  };
  const addCustomColumn = () => {
    const label = customColumnName.trim(); if (!label) return;
    const key = slugify(label);
    if (draftColumns.some((column) => column.key === key)) { notify('Esse campo já existe.', 'error'); return; }
    setDraftColumns((current) => [...current, { key, label, visible: true, width: 160, order: current.length }]); setCustomColumnName('');
  };
  const moveColumn = (index, direction) => setDraftColumns((current) => {
    const target = index + direction; if (target < 0 || target >= current.length) return current;
    const next = [...current]; [next[index], next[target]] = [next[target], next[index]]; return next.map((column, order) => ({ ...column, order }));
  });

  const fieldInput = (row, column) => {
    if (column.key === 'bases_associadas') {
      const names = Array.isArray(row?.base_names) ? row.base_names : [];
      return (
        <div className="db-associated-bases" aria-label={`Bases associadas a ${row.name || 'este contato'}`}>
          {names.length
            ? names.map((name, index) => <span className="db-associated-base" key={`${row.id}-${index}`}>{name}</span>)
            : <span className="db-associated-base-empty">—</span>}
        </div>
      );
    }
    const value = valueForColumn(row, column);
    const isDate = column.key === 'registered_at';
    return <input
      className="db-cell-input"
      type={isDate ? 'date' : 'text'}
      value={isDate ? inputDate(value) : value}
      onChange={(event) => updateInline(row.id, column.key, event.target.value, !EDITABLE_COLUMNS.has(column.key))}
      aria-label={`${column.label} de ${row.name || 'contato'}`}
    />;
  };
  const sortIcon = (key) => sortBy !== key ? <ChevronsUpDown size={14} /> : (sortDir === 'asc' ? <ArrowDownAZ size={14} /> : <ArrowUpAZ size={14} />);

  return (
    <main className={`db-page ${baseIdDaRota ? "db-detail-page" : "db-home-page"} ${ampliado ? 'db-expanded-page' : ''}`}>
      <div className="db-shell">
        <header className="db-page-header">
          <div>
            <div className="db-breadcrumb">{baseIdDaRota ? <><span>CRM</span><ChevronRight size={13} /><strong>Data Base</strong></> : <span className="db-home-eyebrow">ESPAÇO DE TRABALHO</span>}</div>
            <h1>{baseIdDaRota ? 'Bases compartilhadas' : 'Minhas bases'}</h1>
            <p>{baseIdDaRota ? 'Centralize, organize e acompanhe seus leads em um só lugar.' : 'Escolha uma base para abrir e trabalhar com os contatos.'}</p>
          </div>
          <div className="db-header-actions">
            {baseIdDaRota && <button className="db-button db-button-secondary" type="button" onClick={openImport}><Upload size={16} /> Importar</button>}
            {!baseIdDaRota && <button className="db-button db-button-primary" type="button" onClick={() => setBaseDialogOpen(true)}><Plus size={17} /> Nova base</button>}
          </div>
        </header>

        {!baseIdDaRota && (<>
        <section className="db-stats-row" aria-label="Resumo das bases">
          <StatCard icon={BarChart3} label="Contatos únicos" value={uniqueContacts.toLocaleString('pt-BR')} hint="em todas as bases" tone="indigo" />
          <StatCard icon={Archive} label="Total de bases" value={bases.length.toLocaleString('pt-BR')} hint="bases organizadas" tone="teal" />
          <button type="button" className="db-create-stat-button" onClick={() => setBaseDialogOpen(true)} aria-label="Criar nova base">
            <StatCard icon={Plus} label="Nova base" value="Criar base" hint="base vazia" tone="indigo" />
          </button>
          <div className="db-home-filter">
            <label className="db-home-filter-field"><span>Escolha uma base</span><select value={filtroBaseHome} onChange={(event) => setFiltroBaseHome(event.target.value)}><option value="">Todas</option>{bases.map((base) => <option key={base.id} value={base.id}>{base.name}</option>)}</select></label>
            <label className="db-home-filter-search"><Search size={16} /><input value={buscaBaseHome} onChange={(event) => setBuscaBaseHome(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); consultarBaseHome(); } }} placeholder="Pesquisar base pelo nome" /></label>
            <button type="button" className="db-home-filter-button" onClick={consultarBaseHome}><Search size={16} /> Consultar</button>
          </div>
          
        </section>

        <section className="db-section-heading" aria-hidden="true"></section>
        <div className="db-base-grid">
          <button type="button" className={`db-base-card db-total-card ${activeBaseId === 'all' ? 'is-active' : ''}`} onClick={() => navigate('/data-base/all')}>
            <div className="db-base-card-top"><span className="db-base-mark db-mark-total"><BarChart3 size={18} /></span><span className="db-card-badge">VISÃO CONSOLIDADA</span></div>
            <div className="db-base-card-name">Base Total de Leads</div><div className="db-base-card-meta">Todos os contatos únicos em uma visão, com as bases associadas.</div><div className="db-base-card-footer"><strong><span className="db-card-count-icon">#</span>{uniqueContacts.toLocaleString('pt-BR')} contatos únicos</strong><ChevronRight size={17} /></div>
          </button>
          {basesFiltradas.map((base) => (
            <div
              className={`db-base-card-wrap ${String(activeBaseId) === String(base.id) ? 'is-active' : ''}`}
              key={base.id}
            >
              <button
                type="button"
                className={`db-base-card ${String(activeBaseId) === String(base.id) ? 'is-active' : ''}`}
                style={{ '--db-base-color': base.color || '#4D56D8' }}
                onClick={() => abrirBase(base.id)}
              >
                <div className="db-base-card-top">
                  <span
                    className="db-base-mark"
                    style={{ color: base.color || '#4D56D8' }}
                  >
                    <Archive size={18} />
                  </span>

                  <span className="db-card-badge">
                    {Number(base.count || 0).toLocaleString('pt-BR')} contatos
                  </span>
                </div>

                <div className="db-base-card-name">{base.name}</div>

                <div className="db-base-card-meta">
                  Abra para pesquisar, editar e organizar os contatos desta base.
                </div>

                <div className="db-base-card-footer">
                  <strong>Abrir base</strong>
                  <ChevronRight size={17} />
                </div>
              </button>

              <button
                type="button"
                className="db-base-menu-button"
                aria-label={`Opções da base ${base.name}`}
                aria-expanded={menuBaseAberto === base.id}
                onClick={(event) => {
                  event.stopPropagation();
                  setMenuBaseAberto((atual) =>
                    String(atual) === String(base.id) ? null : base.id
                  );
                }}
              >
                <MoreVertical size={17} />
              </button>

              {menuBaseAberto === base.id && (
                <div
                  className="db-base-menu"
                  onClick={(event) => event.stopPropagation()}
                >
                  {canDeleteBase && (
                    <button
                      type="button"
                      className="db-base-menu-edit"
                      onClick={() => abrirEdicaoBase(base)}
                    >
                      <Pencil size={14} />
                      Editar
                    </button>
                  )}

                  {canDeleteBase && (
                    <button
                      type="button"
                      className="db-base-menu-danger"
                      onClick={async () => {
                        setMenuBaseAberto(null);
                        await excluirBase(base);
                      }}
                    >
                      <Trash2 size={14} />
                      Excluir
                    </button>
                  )}

                  {!canDeleteBase && (
                    <span className="db-base-menu-disabled">
                      Exclusão disponível somente para administradores ou Marketing
                    </span>
                  )}
                </div>
              )}
            </div>
          ))}

        </div>
        </>)}

        {baseIdDaRota && (
          <section className="db-detail-header">
            <div className="db-detail-topline">
              <button
                type="button"
                className="db-back-button"
                onClick={() => navigate('/data-base')}
              >
                ← Voltar
              </button>

              <div className="db-detail-breadcrumb">
                <span>CRM</span>
                <span>›</span>
                <span>Data Base</span>
                <span>›</span>
                <strong>{activeBase?.name || 'Base'}</strong>
              </div>
            </div>

            <div className="db-detail-title-row">
              <div>
                <span className="db-eyebrow">GESTÃO DE DADOS</span>
                <h1>{activeBase?.name || 'Base selecionada'}</h1>
                <p>Organize, pesquise e atualize os contatos desta base.</p>
              </div>
            </div>

            <div className="db-detail-stats">
              <StatCard
                icon={UserPlus}
                label="Contatos nesta visão"
                value={total.toLocaleString('pt-BR')}
                hint="contatos cadastrados"
                tone="indigo"
              />
              <StatCard
                icon={Archive}
                label="Bases ativas"
                value={bases.length.toLocaleString('pt-BR')}
                hint="bases organizadas"
                tone="teal"
              />
              <div className="db-detail-status-card">
                <span className="db-detail-status-dot" />
                <div>
                  <span>Status dos dados</span>
                  <strong>Salvo automaticamente</strong>
                  <small>
                    {formatDateTime(activeBase?.updatedAt)}
                    {activeBase?.updated_by_name ? ` · ${activeBase.updated_by_name}` : ''}
                  </small>
                </div>
                <small>Base atualizada</small>
              </div>
            </div>
          </section>
        )}

        <section className="db-contacts-panel">
          <div className="db-panel-heading">
            <div><span className="db-eyebrow">{activeBaseId === 'all' ? 'Visão geral' : 'Base selecionada'}</span><h2>{activeBaseId === 'all' ? 'Base Total de Leads' : activeBase?.name || 'Contatos'}</h2><span className="db-result-count">{total.toLocaleString('pt-BR')} contatos encontrados</span></div>
            <div className="db-panel-actions"><button className="db-button db-button-primary" type="button" onClick={openLead}><UserPlus size={16} /> Novo lead</button></div>
          </div>
          <div className="db-toolbar">
            <label className="db-search"><Search size={17} /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Buscar por nome ou telefone" /><kbd>⌘ K</kbd></label>
            <button className={`db-filter-button ${filtersOpen || hasFilters ? 'is-active' : ''}`} type="button" onClick={() => setFiltersOpen((current) => !current)}><ListFilter size={16} /> Filtros {hasFilters && <span>{[sector, source, dateFrom, dateTo, activeBaseId === 'all' ? associatedBase : ''].filter(Boolean).length + (search ? 1 : 0)}</span>}<ChevronDown size={14} /></button>
              {baseIdDaRota && <button className="db-button db-button-secondary" type="button" onClick={openImport}><Upload size={16} /> Importar</button>}
              {baseIdDaRota && <button className="db-button db-button-ghost" type="button" onClick={openExport}><Download size={16} /> Exportar</button>}
            {activeBaseId !== 'all' && <button className="db-button db-button-ghost" type="button" onClick={openColumns}><Columns3 size={16} /> Colunas</button>}
            <button
              className="db-button db-button-ghost db-expand-button"
              type="button"
              onClick={() => setAmpliado((current) => !current)}
              title={ampliado ? 'Reduzir tabela' : 'Ampliar tabela'}
            >
              {ampliado ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
              {ampliado ? 'Reduzir' : 'Ampliar'}
            </button>
            <button className="db-icon-button db-toolbar-icon" type="button" onClick={refresh} title="Atualizar" disabled={refreshing}><RefreshCw size={16} className={refreshing ? 'db-spin' : ''} /></button>
          </div>
          {filtersOpen && <div className="db-filters-panel">
            {activeBaseId === 'all' && <label><span>Associada à base</span><select value={associatedBase} onChange={(event) => setAssociatedBase(event.target.value)}><option value="">Todas as bases</option>{bases.map((base) => <option key={base.id} value={base.id}>{base.name}</option>)}</select></label>}
            <label><span>Setor</span><select value={sector} onChange={(event) => setSector(event.target.value)}><option value="">Todos os setores</option>{filterOptions.sectors.map((option) => <option key={option} value={option}>{option}</option>)}</select></label>
            <label><span>Origem</span><select value={source} onChange={(event) => setSource(event.target.value)}><option value="">Todas as origens</option>{filterOptions.sources.map((option) => <option key={option} value={option}>{option}</option>)}</select></label>
            <label><span>Cadastro de</span><input type="date" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} /></label>
            <label><span>Cadastro até</span><input type="date" value={dateTo} onChange={(event) => setDateTo(event.target.value)} /></label>
            {hasFilters && <button className="db-text-button db-clear-filter" type="button" onClick={clearFilters}><X size={14} /> Limpar tudo</button>}
          </div>}
          {selectedIds.size > 0 && <div className="db-selection-bar"><span><Check size={16} /> {selectedIds.size} selecionado(s)</span><div><button className="db-button db-button-ghost" type="button" onClick={openExport}><Download size={15} /> Exportar seleção</button><button className="db-button db-button-danger" type="button" onClick={() => removeContacts([...selectedIds])}><Trash2 size={15} /> {activeBaseId === 'all' ? 'Excluir definitivamente' : 'Remover da base'}</button></div></div>}
          {error && <div className="db-error"><span>{error}</span><button type="button" onClick={refresh}>Tentar novamente</button></div>}
          <div className="db-table-wrap">
            <table className="db-table">
              <colgroup><col className="db-check-col" />{visibleColumns.map((column) => <col key={column.key} style={{ minWidth: column.width, width: column.width }} />)}<col className="db-actions-col" /></colgroup>
              <thead><tr><th className="db-check-col"><input type="checkbox" checked={allPageSelected} onChange={togglePageSelection} aria-label="Selecionar página" /></th>{visibleColumns.map((column) => <th key={column.key}><button type="button" className={`db-sort-button ${SORTABLE_COLUMNS.has(column.key) ? '' : 'is-static'}`} onClick={() => changeSort(column.key)}>{column.label}{SORTABLE_COLUMNS.has(column.key) && sortIcon(column.key)}</button></th>)}<th className="db-actions-col"><span className="sr-only">Ações</span></th></tr></thead>
              <tbody>
                {loading ? <tr><td colSpan={visibleColumns.length + 2} className="db-table-loading"><Loader2 className="db-spin" size={22} /> Carregando contatos...</td></tr> : items.length === 0 ? <tr><td colSpan={visibleColumns.length + 2}><EmptyState searchActive={hasFilters} onClear={clearFilters} /></td></tr> : items.map((row) => <tr key={row.id} className={selectedIds.has(row.id) ? 'is-selected' : ''}>
                  <td className="db-check-col"><input type="checkbox" checked={selectedIds.has(row.id)} onChange={() => toggleSelection(row.id)} aria-label={`Selecionar ${row.name || 'contato'}`} /></td>
                  {visibleColumns.map((column) => <td key={`${row.id}-${column.key}`} className={column.key === 'name' ? 'db-name-cell' : ''}>{column.key === 'registered_at' ? fieldInput(row, column) : fieldInput(row, column)}</td>)}
                  <td className="db-actions-col"><button type="button" className="db-row-action" onClick={() => removeContacts([row.id])} title={activeBaseId === 'all' ? 'Excluir definitivamente' : 'Remover desta base'}><Trash2 size={15} /></button></td>
                </tr>)}
              </tbody>
            </table>
          </div>
          <footer className="db-pagination"><span>Mostrando {items.length ? ((page - 1) * pageSize) + 1 : 0}–{Math.min(page * pageSize, total)} de {total.toLocaleString('pt-BR')}</span><div className="db-pagination-actions"><label>Itens por página <select value={pageSize} onChange={(event) => { setPageSize(Number(event.target.value)); setPage(1); }} aria-label="Itens por página">{PAGE_SIZES.map((size) => <option key={size} value={size}>{size}</option>)}</select></label><button type="button" className="db-icon-button" disabled={page <= 1} onClick={() => setPage((current) => current - 1)}><ChevronLeft size={16} /></button><span className="db-page-number">{page} / {pageCount}</span><button type="button" className="db-icon-button" disabled={page >= pageCount} onClick={() => setPage((current) => current + 1)}><ChevronRight size={16} /></button></div></footer>
        </section>
      </div>

      {leadOpen && <Modal title="Novo lead" eyebrow="Cadastro de contato" onClose={() => setLeadOpen(false)} className="db-form-modal"><form onSubmit={submitLead}>
        <p className="db-modal-copy">Cadastre um contato na base escolhida. Se o telefone já existir, o contato será atualizado sem duplicação.</p>
        <div className="db-form-grid"><label className="db-field db-field-full"><span>Nome <em>*</em></span><input autoFocus value={leadForm.name} onChange={(event) => setLeadForm({ ...leadForm, name: event.target.value })} placeholder="Nome completo" required /></label><label className="db-field"><span>Telefone</span><input value={leadForm.phone} onChange={(event) => setLeadForm({ ...leadForm, phone: event.target.value })} placeholder="(00) 00000-0000" /></label><label className="db-field"><span>Setor</span><input value={leadForm.sector} onChange={(event) => setLeadForm({ ...leadForm, sector: event.target.value })} placeholder="Ex.: Comercial" /></label><label className="db-field"><span>Data de cadastro</span><input type="date" value={leadForm.registered_at} onChange={(event) => setLeadForm({ ...leadForm, registered_at: event.target.value })} /></label><label className="db-field"><span>Origem</span><input value={leadForm.source} onChange={(event) => setLeadForm({ ...leadForm, source: event.target.value })} placeholder="Ex.: Indicação" /></label><label className="db-field db-field-full"><span>Base destino</span><select value={leadForm.base_id} onChange={(event) => setLeadForm({ ...leadForm, base_id: event.target.value })} required><option value="">Selecione uma base</option>{bases.map((base) => <option key={base.id} value={base.id}>{base.name}</option>)}</select></label><label className="db-field db-field-full"><span>Observações</span><textarea rows="4" value={leadForm.observations} onChange={(event) => setLeadForm({ ...leadForm, observations: event.target.value })} placeholder="Notas importantes sobre o contato" /></label></div>
        <div className="db-modal-footer"><button className="db-button db-button-ghost" type="button" onClick={() => setLeadOpen(false)}>Cancelar</button><button className="db-button db-button-primary" type="submit" disabled={savingLead}>{savingLead && <Loader2 className="db-spin" size={16} />} Salvar lead</button></div>
      </form></Modal>}

      {baseDialogOpen && <Modal title="Nova base" eyebrow="Organização" onClose={() => setBaseDialogOpen(false)} className="db-small-modal"><form onSubmit={submitBase}><p className="db-modal-copy">Crie um espaço para segmentar seus contatos e acompanhar sua operação.</p><label className="db-field"><span>Nome da base</span><input autoFocus value={baseName} onChange={(event) => setBaseName(event.target.value)} placeholder="Ex.: Leads inbound" required /></label><div className="db-modal-footer"><button className="db-button db-button-ghost" type="button" onClick={() => setBaseDialogOpen(false)}>Cancelar</button><button className="db-button db-button-primary" type="submit"><Plus size={16} /> Criar base</button></div></form></Modal>}

      {editBaseOpen && editBase && <Modal title="Editar base" eyebrow="Organização" onClose={() => !savingBaseEdit && setEditBaseOpen(false)} className="db-small-modal"><form onSubmit={salvarEdicaoBase}><label className="db-field"><span>Nome da base</span><input autoFocus value={editBaseName} onChange={(event) => setEditBaseName(event.target.value)} maxLength={160} required /></label><div className="db-field"><span>Cor da base</span><div className="db-color-options" role="radiogroup" aria-label="Cor da base">{BASE_COLORS.map((cor) => <button key={cor} type="button" role="radio" aria-checked={editBaseColor.toUpperCase() === cor} aria-label={`Cor ${cor}`} className={`db-color-swatch ${editBaseColor.toUpperCase() === cor ? 'is-selected' : ''}`} style={{ background: cor }} onClick={() => setEditBaseColor(cor)}>{editBaseColor.toUpperCase() === cor && <Check size={14} />}</button>)}<label className={`db-color-custom ${!BASE_COLORS.includes(editBaseColor.toUpperCase()) ? 'is-selected' : ''}`} title="Escolher outra cor" style={!BASE_COLORS.includes(editBaseColor.toUpperCase()) ? { background: editBaseColor } : undefined}><input type="color" value={editBaseColor} onChange={(event) => setEditBaseColor(event.target.value.toUpperCase())} aria-label="Escolher outra cor" /><span>+</span></label></div></div><div className="db-modal-footer"><button className="db-button db-button-ghost" type="button" onClick={() => setEditBaseOpen(false)} disabled={savingBaseEdit}>Cancelar</button><button className="db-button db-button-primary" type="submit" disabled={savingBaseEdit}>{savingBaseEdit && <Loader2 className="db-spin" size={16} />} Salvar alterações</button></div></form></Modal>}

      {importOpen && <Modal title="Importar contatos" eyebrow="Importação assistida" onClose={() => !importBusy && setImportOpen(false)} className="db-import-modal">
        <div className="db-stepper"><div className={importStep === 'file' ? 'is-current' : 'is-done'}><span>1</span><small>Arquivo</small></div><div className={importStep === 'mapping' ? 'is-current' : importStep === 'result' ? 'is-done' : ''}><span>2</span><small>Mapear colunas</small></div><div className={importStep === 'result' ? 'is-current' : ''}><span>3</span><small>Validar e importar</small></div></div>
        {importStep === 'file' && <div className="db-upload-zone"><input id="db-import-file" type="file" accept=".csv,.xlsx,.xls" onChange={readImportFile} /><label htmlFor="db-import-file"><span className="db-upload-icon"><FileSpreadsheet size={25} /></span><strong>Escolha um arquivo CSV ou XLSX</strong><small>Arraste ou clique para procurar no seu computador</small><em>Sem dados de demonstração: só os seus registros serão importados.</em></label></div>}
        {importStep === 'mapping' && <><div className="db-import-file-name"><FileSpreadsheet size={16} /><span>{importFile?.name}</span><small>{importRows.length} registros encontrados</small></div><div className="db-import-target"><label className="db-field"><span>Base de destino</span><select value={importBaseId} onChange={(event) => setImportBaseId(event.target.value)} required><option value="">Selecione uma base</option>{bases.map((base) => <option key={base.id} value={base.id}>{base.name}</option>)}</select></label></div><p className="db-modal-copy">Associe os campos à coluna correspondente no arquivo. Nome é obrigatório; telefone é opcional e ajuda a evitar duplicidade.</p><div className="db-mapping-list">{IMPORT_FIELDS.map((field) => <label key={field.key} className="db-mapping-row"><span>{field.label} {field.required && <em>*</em>}</span><select value={importMapping[field.key] || ''} onChange={(event) => setImportMapping({ ...importMapping, [field.key]: event.target.value })}><option value="">Não importar</option>{importHeaders.map((header) => <option key={header} value={header}>{header}</option>)}</select></label>)}</div><div className="db-modal-footer"><button className="db-button db-button-ghost" type="button" onClick={() => setImportStep('file')}>Voltar</button><button className="db-button db-button-primary" type="button" onClick={validateImport} disabled={importBusy}>{importBusy && <Loader2 className="db-spin" size={16} />} Validar arquivo</button></div></>}
        {importStep === 'result' && <><div className="db-validation-result"><div className="db-validation-icon"><Check size={20} /></div><div><strong>Validação concluída</strong><p>Os registros válidos podem ser importados. Telefones repetidos serão atualizados, não duplicados.</p></div></div><div className="db-import-summary"><div><strong>{importResult?.validRows?.length ?? importRecords.length}</strong><span>registros válidos</span></div><div><strong>{importResult?.invalidRows?.length ?? 0}</strong><span>com inconsistências</span></div><div><strong>{(importResult?.duplicates || []).length}</strong><span>telefones repetidos</span></div></div>{((importResult?.invalidRows || []).length > 0 || (importResult?.duplicates || []).length > 0) && <pre className="db-validation-details">{JSON.stringify({ invalidRows: importResult.invalidRows || [], duplicates: importResult.duplicates || [] }, null, 2)}</pre>}<div className="db-modal-footer"><button className="db-button db-button-ghost" type="button" onClick={() => setImportStep('mapping')}>Voltar e corrigir</button><button className="db-button db-button-primary" type="button" onClick={commitImport} disabled={importBusy || !importRecords.length}>{importBusy && <Loader2 className="db-spin" size={16} />} Importar {importRecords.length} válidos</button></div></>}
      </Modal>}

      {exportOpen && <Modal title="Exportar contatos" eyebrow="Saída de dados" onClose={() => !exportBusy && setExportOpen(false)} className="db-export-modal"><p className="db-modal-copy">Escolha o recorte e as colunas que deseja levar para o arquivo.</p><div className="db-export-options"><span className="db-option-label">Registros</span><label className="db-radio-card"><input type="radio" checked={exportMode === 'all'} onChange={() => setExportMode('all')} /><span><strong>Todos da base</strong><small>Ignora os filtros atuais</small></span></label><label className="db-radio-card"><input type="radio" checked={exportMode === 'filtered'} onChange={() => setExportMode('filtered')} /><span><strong>Resultado filtrado</strong><small>Usa busca, filtros e ordenação atuais</small></span></label><label className="db-radio-card"><input type="radio" checked={exportMode === 'selected'} onChange={() => setExportMode('selected')} disabled={!selectedIds.size} /><span><strong>Selecionados</strong><small>{selectedIds.size ? `${selectedIds.size} selecionado(s)` : 'Selecione contatos na tabela'}</small></span></label></div><div className="db-export-columns"><div className="db-export-columns-head"><span className="db-option-label">Colunas</span><button type="button" className="db-text-button" onClick={() => setExportColumns(exportColumns.length === visibleColumns.length ? [] : visibleColumns.map((column) => column.key))}>{exportColumns.length === visibleColumns.length ? 'Desmarcar todas' : 'Marcar todas'}</button></div><div className="db-column-checks">{visibleColumns.map((column) => <label key={column.key}><input type="checkbox" checked={exportColumns.includes(column.key)} onChange={() => setExportColumns((current) => current.includes(column.key) ? current.filter((key) => key !== column.key) : [...current, column.key])} />{column.label}</label>)}</div></div><div className="db-format-row"><span className="db-option-label">Formato</span><label><input type="radio" checked={exportFormat === 'xlsx'} onChange={() => setExportFormat('xlsx')} /> Excel (.xlsx)</label><label><input type="radio" checked={exportFormat === 'csv'} onChange={() => setExportFormat('csv')} /> CSV</label></div><div className="db-modal-footer"><button className="db-button db-button-ghost" type="button" onClick={() => setExportOpen(false)}>Cancelar</button><button className="db-button db-button-primary" type="button" onClick={runExport} disabled={exportBusy}>{exportBusy && <Loader2 className="db-spin" size={16} />} Gerar arquivo</button></div></Modal>}

      {columnsOpen && <Modal title="Configurar colunas" eyebrow={activeBase?.name} onClose={() => !savingColumns && setColumnsOpen(false)} className="db-columns-modal"><p className="db-modal-copy">Escolha o que aparece na tabela, ajuste a largura e organize a ordem dos campos desta base.</p><div className="db-columns-list">{draftColumns.map((column, index) => <div className={`db-column-row ${!column.visible ? 'is-hidden' : ''}`} key={column.key}><GripVertical size={16} className="db-grip" /><div className="db-column-name"><input className="db-column-label" aria-label={`Nome exibido para ${column.key}`} maxLength={64} value={column.label} onChange={(event) => setDraftColumns((current) => current.map((item) => item.key === column.key ? { ...item, label: event.target.value } : item))} /><small>{column.key}</small></div><label className="db-width-field"><span>Largura</span><input type="number" min="100" max="480" value={column.width} onChange={(event) => setDraftColumns((current) => current.map((item) => item.key === column.key ? { ...item, width: Number(event.target.value) || 100 } : item))} /></label><button type="button" className="db-column-visibility" onClick={() => setDraftColumns((current) => current.map((item) => item.key === column.key ? { ...item, visible: !item.visible } : item))}>{column.visible ? 'Visível' : 'Oculta'}</button><div className="db-move-buttons"><button type="button" onClick={() => moveColumn(index, -1)} disabled={index === 0} aria-label="Mover para cima"><ChevronDown size={14} className="db-chevron-up" /></button><button type="button" onClick={() => moveColumn(index, 1)} disabled={index === draftColumns.length - 1} aria-label="Mover para baixo"><ChevronDown size={14} /></button></div></div>)}</div><div className="db-add-column"><label className="db-field"><span>Adicionar campo customizado</span><input value={customColumnName} onChange={(event) => setCustomColumnName(event.target.value)} onKeyDown={(event) => event.key === 'Enter' && (event.preventDefault(), addCustomColumn())} placeholder="Ex.: Perfil ideal" /></label><button type="button" className="db-button db-button-ghost" onClick={addCustomColumn}><Plus size={15} /> Adicionar</button></div><div className="db-modal-footer"><button className="db-button db-button-ghost" type="button" onClick={() => setColumnsOpen(false)}>Cancelar</button><button className="db-button db-button-primary" type="button" onClick={saveColumns} disabled={savingColumns}>{savingColumns && <Loader2 className="db-spin" size={16} />} Salvar configuração</button></div></Modal>}

      {notice && <div className={`db-toast db-toast-${notice.type}`} role="status"><span>{notice.type === 'error' ? <X size={16} /> : <Check size={16} />}</span>{notice.message}</div>}
    </main>
  );
}
